import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { FocusLayout } from "./components/FocusLayout.js";
import { Shell } from "./components/Shell.js";
import { common } from "./copy/common.js";
import { useSession } from "./session.js";
import { WelcomeScreen } from "./screens/Welcome.js";
import { RegisterScreen } from "./screens/Register.js";
import { LoginScreen } from "./screens/Login.js";
import { RecoveryScreen } from "./screens/Recovery.js";
import { PinRecoverScreen, PinResetScreen } from "./screens/PinForgot.js";
import { HomeScreen } from "./screens/Home.js";
import { ListNewScreen } from "./screens/ListNew.js";
import { ListDetailScreen } from "./screens/ListDetail.js";
import { ListEditScreen } from "./screens/ListEdit.js";
import { ArchiveScreen } from "./screens/Archive.js";
import { ReviewScreen } from "./screens/Review.js";
import { PrintScreen } from "./screens/Print.js";
import { PracticeScreen } from "./screens/Practice.js";
import { MeScreen } from "./screens/Me.js";
import { JoinScreen } from "./screens/Join.js";
import { ClassScreen } from "./screens/Class.js";

function Guard({ children }: { children: ReactNode }) {
  const { ready, user } = useSession();
  if (!ready) return <p className="p-8 text-center text-ink-soft">{common.loading}</p>;
  if (!user) return <Navigate to="/welcome" replace />;
  return children;
}

function Guest({ children }: { children: ReactNode }) {
  const { ready, user } = useSession();
  const location = useLocation();
  if (!ready) return <p className="p-8 text-center text-ink-soft">{common.loading}</p>;
  // A class link opened while logged in joins with the same account (#48).
  if (user && location.pathname === "/register" && new URLSearchParams(location.search).has("join")) {
    return <Navigate to={`/join${location.hash}`} replace />;
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/welcome"
        element={
          <Guest>
            <WelcomeScreen />
          </Guest>
        }
      />
      <Route
        path="/register"
        element={
          <Guest>
            <RegisterScreen />
          </Guest>
        }
      />
      <Route
        path="/login"
        element={
          <Guest>
            <LoginScreen />
          </Guest>
        }
      />
      <Route
        path="/pin/recover"
        element={
          <Guest>
            <PinRecoverScreen />
          </Guest>
        }
      />
      <Route
        path="/pin/reset"
        element={
          <Guest>
            <PinResetScreen />
          </Guest>
        }
      />
      <Route path="/recovery" element={<RecoveryScreen />} />
      {/* Outside the Shell on purpose: a round has one way out, «Abbrechen»
          (docs/UX.md §1). */}
      {["/practice/:listId", "/practice/due/:lang", "/practice/review"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Guard>
              <FocusLayout>
                <PracticeScreen />
              </FocusLayout>
            </Guard>
          }
        />
      ))}
      {/* Outside the Shell: the navigation has no place on paper. */}
      <Route
        path="/lists/:id/print"
        element={
          <Guard>
            <FocusLayout>
              <PrintScreen />
            </FocusLayout>
          </Guard>
        }
      />
      <Route
        element={
          <Guard>
            <Shell />
          </Guard>
        }
      >
        <Route path="/" element={<HomeScreen />} />
        <Route path="/lists/new" element={<ListNewScreen />} />
        <Route path="/lists/archive" element={<ArchiveScreen />} />
        <Route path="/review" element={<ReviewScreen />} />
        <Route path="/lists/:id" element={<ListDetailScreen />} />
        <Route path="/lists/:id/edit" element={<ListEditScreen />} />
        <Route path="/me" element={<MeScreen />} />
        <Route path="/join" element={<JoinScreen />} />
        <Route path="/class" element={<ClassScreen />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
